import { Box, Stack, useTheme } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { GoalsKeys } from "web_myplann/Api/Goals/GoalsKeys";
import { GoalActionBlockedDrawer } from "web_myplann/Goals/Components/GoalActionBlockedDrawer";
import { GoalCreatedPinDrawer } from "web_myplann/Goals/Components/GoalCreatedPinDrawer";
import { GoalDetailComposition } from "web_myplann/Goals/Components/GoalDetailComposition";
import { GoalDetailExtraContribution } from "web_myplann/Goals/Components/GoalDetailExtraContribution";
import { GoalDetailHeaderActions } from "web_myplann/Goals/Components/GoalDetailHeaderActions";
import { GoalDetailHero } from "web_myplann/Goals/Components/GoalDetailHero";
import { GoalDetailLoanCalculator } from "web_myplann/Goals/Components/GoalDetailLoanCalculator";
import { GoalDetailRentPlan } from "web_myplann/Goals/Components/GoalDetailRentPlan";
import { GoalDetailSkeleton } from "web_myplann/Goals/Components/GoalDetailSkeleton";
import { GoalPinningSection } from "web_myplann/Goals/Components/GoalPinningSection";
import { SetOwnFundsDrawer } from "web_myplann/Goals/Components/SetOwnFundsDrawer";
import { withLockGuard } from "web_myplann/Goals/Helpers/withLockGuard";
import { useGoalActionLock } from "web_myplann/Goals/Hooks/useGoalActionLock";
import { useGoalDetail } from "web_myplann/Goals/Hooks/useGoalDetail";
import { useSetOwnFunds } from "web_myplann/Goals/Hooks/useSetOwnFunds";
import { useWelcomePin } from "web_myplann/Goals/Hooks/useWelcomePin";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import { MyPlannNavigationHeader } from "web_myplann/Shared/Header/MyPlannNavigationHeader";
import { useNotFoundRedirect } from "web_myplann/Shared/Hooks/useNotFoundRedirect";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";

export const GoalDetailPage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const routeParams = useParams<{ id: string }>();
	const goalId = getTypedParam(routeParams, "id", Number) ?? null;

	const { goal, dto: goalDto, isLoading, isNotFound } = useGoalDetail(goalId);
	useNotFoundRedirect({
		isNotFound,
		message: t(Resources.Goals.Detail.Actions.notFoundToast),
		defaultRoute: Routing.Goals.GoalsList.path(),
		invalidateQueryKeys: [GoalsKeys.lists()],
	});
	const {
		blockedVariant,
		openBlocked,
		closeBlocked,
		requestEdit,
		onLockedAction,
		messageAdvisor,
	} = useGoalActionLock(goal);
	const {
		welcomePinOpen,
		welcomePinOwnFunds,
		welcomePinTrigger,
		closeWelcomePin,
		assignWelcomePin,
		clearWelcomePinTrigger,
		triggerWelcomePin,
	} = useWelcomePin();
	const [ownFundsOpen, setOwnFundsOpen] = useState(false);

	const { saveOwnFunds, isSaving } = useSetOwnFunds({
		goalId,
		goalDto,
		onSaved: () => {
			setOwnFundsOpen(false);
			triggerWelcomePin(true);
		},
	});

	return (
		<MyPlannLayout
			bgColor={theme.palette.surface.muted}
			disableContentPadding
			topBar={
				<MyPlannNavigationHeader
					title={t(Resources.Goals.Detail.title)}
					bgColor={theme.palette.background.paper}
					defaultRoute={Routing.Goals.GoalsList.path()}
					endComponent={
						goal &&
						goalId !== null && (
							<GoalDetailHeaderActions
								goal={goal}
								goalId={goalId}
								onEdit={requestEdit}
								onBlocked={() => openBlocked("locked")}
							/>
						)
					}
				/>
			}
		>
			{isLoading && !goal && <GoalDetailSkeleton />}

			{goal && (
				<Stack>
					<GoalDetailHero
						goal={goal}
						onAddOwnFunds={withLockGuard(onLockedAction, () =>
							setOwnFundsOpen(true),
						)}
					/>
					<GoalPinningSection
						goal={goal}
						goalDto={goalDto}
						welcomePinTrigger={welcomePinTrigger}
						onWelcomePinTriggerHandled={clearWelcomePinTrigger}
						onLockedAction={onLockedAction}
					/>
					<Box mt={1}>
						<GoalDetailLoanCalculator goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailRentPlan goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailExtraContribution goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailComposition goal={goal} />
					</Box>
				</Stack>
			)}
			<GoalActionBlockedDrawer
				open={blockedVariant !== null}
				variant={blockedVariant ?? "locked"}
				onClose={closeBlocked}
				onMessageAdvisor={messageAdvisor}
			/>
			<SetOwnFundsDrawer
				open={ownFundsOpen}
				isSaving={isSaving}
				onClose={() => setOwnFundsOpen(false)}
				onSubmit={saveOwnFunds}
			/>
			<GoalCreatedPinDrawer
				open={welcomePinOpen}
				ownFunds={welcomePinOwnFunds}
				onClose={closeWelcomePin}
				onAssign={assignWelcomePin}
			/>
		</MyPlannLayout>
	);
};
